/* ============================================================================
 * svnty-pdp-sticky.js — THE STICKY BUY BAR (SVNTY, 2026-09-19).
 * Ported from the sibling's sib-pdp-sticky.js (the sibling lessons of 2026-09-18, Sec 5.2),
 * re-skinned to the 8/6/4 ladder and rewired from Horizon to Trade's picker.
 * Classic deferred script, no deps, ASCII only, no console.
 *
 * THE BAR: one 44px row - the photograph of the colour on screen, the SIZE chip,
 * the COLOUR chip, the counter, ADD TO BAG. The chips open THE MENU UPWARD (200ms
 * in, 170ms out, one open at a time, outside tap and Escape close it, roving focus
 * inside). The size guide is the last row of the size menu.
 *
 * ONE TRUTH WITH THE PAGE: a choice in the bar clicks the real radio (or sets the
 * real select) in Trade's variant-selects, so the gallery, the price, the Ref line
 * and the form follow; the bar follows the page back through the bubbled change and
 * through PUB_SUB_EVENTS.variantChange.
 * THE COLOUR PIPELINE (D4-25): a COLOUR picked in the bar goes through the one setter,
 * window.SVNTY.colour.set(colour, 'sticky'), so the whole page follows it (gallery,
 * buy box, WEAR IT WITH, SIMILAR PRODUCTS); the bar hears every colour change on
 * 'svnty:colour' and repaints its picture (the colour's variant picture, else the
 * colour's first gallery picture) and its colour chip. A size pick still clicks the
 * real size input directly: sizes are not the bus's business.
 *
 * THE PRESENCE: shown only while the page's own ADD TO BAG is off screen
 * (IntersectionObserver with hysteresis), never over an open drawer or dialog (the
 * CSS reads body.overflow-hidden*), and it publishes its own height as
 * --svnty-sticky-h on :root so the page reserves exactly that much. Safe-area
 * padding and box-sizing are a PAIR (THE COVER, iOS law IOS-10/11). We never write
 * to the bar's own layer during a gesture: only its CONTENTS change.
 * ========================================================================== */
(function () {
  'use strict';

  /* WORDS (H4): every sentence comes from the server-printed `data-*` labels or, as the
     pre-render fallback, from `window.svntyStrings` (snippets/svnty-head.liquid, rendered by
     Liquid from `locales/*.json`). No English literal lives in this file. */
  var STR = window.svntyStrings || {};

  var FLASH_MS = 1300;
  var MENU_CLOSE_MS = 170;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function cartAddUrl() {
    try {
      if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    } catch (e) {}
    return '/cart/add';
  }

  function cartUrl() {
    try {
      if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    } catch (e) {}
    return '/cart';
  }

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function withWidth(url, width) {
    if (typeof url !== 'string' || !url) return '';
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function productSection() {
    return document.querySelector('section[id^="MainProduct-"]');
  }

  /* P17-26 (eye pass R2 P1-1, C-04 on the page): no size is chosen for the shopper. The section carries the door's no-size state
     from Liquid until a size is tapped (svnty-pdp.js takes it off); while it does, the bar's size stays empty ("Size") and its
     ADD TO BAG asks for the size (onAdd opens the size menu), never adds the size Trade pre-checked */
  function sizeOpen() {
    var section = productSection();
    return !!(section && section.classList.contains('svnty-door--nosize'));
  }

  function variantSelects() {
    var section = productSection();
    return section ? section.querySelector('variant-selects') : null;
  }

  /* the wrapper of one option in Trade's picker, found by the name SVNTY prints on it */
  function optionWrapper(optionName) {
    var selects = variantSelects();
    if (!selects || !optionName) return null;
    var wraps = selects.querySelectorAll('.product-form__input');
    for (var i = 0; i < wraps.length; i += 1) {
      if (wraps[i].dataset.svntyOption === optionName) return wraps[i];
    }
    return null;
  }

  function pageValue(optionName) {
    var wrap = optionWrapper(optionName);
    if (!wrap) return '';
    var select = wrap.querySelector('select');
    if (select) return select.value;
    var radios = wrap.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i += 1) {
      if (radios[i].checked) return radios[i].value;
    }
    return '';
  }

  /* click the real option in Trade's picker; true when it existed */
  function clickPageOption(optionName, value) {
    var wrap = optionWrapper(optionName);
    if (!wrap || !value) return false;
    var radios = wrap.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i += 1) {
      if (radios[i].value === value) {
        if (radios[i].checked) {
          /* the pre-checked size picked in the bar is the shopper's choice: the page hears the tap and leaves its no-size state */
          if (wrap.closest('.svnty-door--nosize')) radios[i].click();
          return true;
        }
        radios[i].click();
        return true;
      }
    }
    var select = wrap.querySelector('select');
    if (select) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  class SvntyPdpSticky extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._abort = new AbortController();
      var signal = this._abort.signal;

      var data = this.parseData();
      this.variants = data.variants;
      this.rows = data.rows;
      this.colours = data.colours;
      this.hasColour = this.dataset.hasColour === 'true' && this.colours.length > 0;
      this.single = this.dataset.single === 'true';
      this.rowOption = this.dataset.rowOption || '';
      this.colourOption = this.dataset.colourOption || '';
      this.rowLabel = this.dataset.rowLabel || STR.size_label || '';
      this.colourLabel = this.dataset.colourLabel || STR.colour_label || '';
      this.busy = false;
      this.quantity = 1;
      this.openPick = null;

      this.addBtn = this.querySelector('[data-svnty-pdpbar-add]');
      this.status = this.querySelector('.svnty-pdpbar__status');
      this.img = this.querySelector('[data-svnty-pdpbar-img]');
      this.qtyVal = this.querySelector('[data-svnty-qty-val]');
      this.qtyDec = this.querySelector('[data-svnty-qty-dec]');
      this.qtyInc = this.querySelector('[data-svnty-qty-inc]');
      this.rowPick = this.querySelector('[data-svnty-pick="row"]');
      this.colourPick = this.querySelector('[data-svnty-pick="colour"]');
      this.addLabel = (this.addBtn && this.addBtn.dataset.addLabel) || STR.add_to_bag || '';
      this.addedLabel = (this.addBtn && this.addBtn.dataset.addedLabel) || STR.added || '';
      this.soldLabel = (this.addBtn && this.addBtn.dataset.soldoutLabel) || STR.sold_out || '';
      this.selectLabel =
        (this.addBtn && this.addBtn.dataset.selectLabel) ||
        (STR.select_option || '').replace('[option]', (this.rowLabel || '').toLowerCase());

      var current = this.currentVariant();
      this.selectedColour = current ? current.colour : this.firstColour();
      this.selectedRow = current && current.available && !sizeOpen() ? current.row : '';

      if (this.addBtn) {
        this.addBtn.addEventListener('click', function () { self.onAdd(); }, { signal: signal });
    }
      if (this.qtyDec) {
        this.qtyDec.addEventListener('click', function () { self.stepQty(-1); }, { signal: signal });
    }
      if (this.qtyInc) {
        this.qtyInc.addEventListener('click', function () { self.stepQty(1); }, { signal: signal });
    }
      var guide = this.querySelector('[data-svnty-size-guide]');
      if (guide) {
        guide.addEventListener('click', function (event) {
          event.preventDefault();
          self.closePick();
          document.dispatchEvent(new CustomEvent('svnty:open-size-guide'));
        }, { signal: signal });
    }

      this.wirePick(this.rowPick, 'row');
      this.wirePick(this.colourPick, 'colour');

      /* one open menu; outside tap and Escape close it */
      document.addEventListener('pointerdown', function (event) {
        if (!self.openPick) return;
        if (self.openPick.contains(event.target)) return;
        self.closePick();
      }, { signal: signal, capture: true });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && self.openPick) {
          var trigger = self.openPick.querySelector('[data-svnty-pick-trigger]');
          self.closePick();
          if (trigger) trigger.focus();
        }
      }, { signal: signal });

      /* the page leads, the bar follows */
      var selects = variantSelects();
      if (selects) {
        /* P17-26: a task later, after svnty-pdp.js has taken the no-size state off for the size just tapped */
        selects.addEventListener('change', function () { window.setTimeout(function () { self.followPage(); }, 0); }, { signal: signal });
    }
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.variantChange) {
          this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function () { self.followPage(); });
        }
      } catch (e) {}
      /* THE COLOUR PIPELINE: every colour change on the page, whoever made it */
      document.addEventListener('svnty:colour', function () { self.followPage(); }, { signal: signal });
      /* P17-26 (eye pass R2 P1-1): the page's first size tap, even on the size Trade pre-checked (no change event) */
      document.addEventListener('svnty:size:chosen', function () { self.followPage(); }, { signal: signal });

      this.renderPicks();
      this.renderImage();
      if (this.img) this.img.setAttribute('data-svnty-colour', this.selectedColour || '');
      this.updateAddButton();
      this.renderQty();

      /* the exact bar height, so the page reserves exactly that much */
      this._publishHeight = function () {
        var height = self.offsetHeight || 0;
        if (height > 0) document.documentElement.style.setProperty('--svnty-sticky-h', height + 'px');
      };
      document.body.classList.add('svnty-has-pdpbar');
      this._publishHeight();
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(this._publishHeight);
        this._resizeObserver.observe(this);
    }
      window.addEventListener('resize', this._publishHeight, { signal: signal });

      this.observeBuyBox();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._buyBoxObserver) this._buyBoxObserver.disconnect();
      if (this._unsubscribe) this._unsubscribe();
      document.body.classList.remove('svnty-has-pdpbar');
    }

    /* --- data ----------------------------------------------------------------- */

    parseData() {
      var out = { variants: [], rows: [], colours: [] };
      var tag = this.querySelector('.svnty-pdpbar__data');
      if (!tag) return out;
      try {
        var parsed = JSON.parse(tag.textContent || '{}');
        out.variants = Array.isArray(parsed.variants) ? parsed.variants : [];
        out.rows = Array.isArray(parsed.rows) ? parsed.rows : [];
        out.colours = Array.isArray(parsed.colours) ? parsed.colours : [];
      } catch (e) {}
      return out;
    }

    currentVariant() {
      var id = this.dataset.currentId;
      if (!id) return null;
      return this.variants.find(function (v) { return String(v.id) === String(id); }) || null;
    }

    firstColour() {
      if (!this.hasColour) return '';
      var v = this.variants.find(function (x) { return x.available; }) || this.variants[0];
      return v ? v.colour : '';
    }

    colourInfo(name) {
      return this.colours.find(function (c) { return c.name === name; }) || null;
    }

    rowAvailable(row) {
      var self = this;
      var v = this.variants.find(function (x) {
        return (self.hasColour ? x.colour === self.selectedColour : true) && x.row === row;
      });
      return !!(v && v.available);
    }

    colourAvailable(colour) {
      return this.variants.some(function (x) { return x.colour === colour && x.available; });
    }

    resolveVariant() {
      var self = this;
      if (this.single) {
        return this.variants.find(function (x) { return x.available; }) || this.variants[0] || null;
    }
      if (this.rows.length && !this.selectedRow) return null;
      return (
        this.variants.find(function (x) {
          return (
            (self.hasColour ? x.colour === self.selectedColour : true) &&
            (self.rows.length ? x.row === self.selectedRow : true)
          );
        }) || null
      );
    }

    /* --- following the page --------------------------------------------------- */

    followPage() {
      if (this.rowOption) {
        var row = pageValue(this.rowOption);
        if (row && !sizeOpen()) this.selectedRow = row;
    }
      if (this.hasColour && this.colourOption) {
        var colour = pageValue(this.colourOption);
        if (colour) this.selectedColour = colour;
    }
      var resolved = this.resolveVariant();
      if (resolved) this.dataset.currentId = String(resolved.id);
      this.renderPicks();
      this.renderImage();
      this.clearStatus();
      this.updateAddButton();
      var self = this;
      window.setTimeout(function () { self.observeBuyBox(); }, 350);
    }

    /* --- THE PRESENCE --------------------------------------------------------- */

    observeBuyBox() {
      var self = this;
      if (!('IntersectionObserver' in window)) return;
      var section = productSection();
      var box = section ? section.querySelector('.product-form__buttons') : null;
      if (!box) return;
      if (this._buyBoxObserver && this._buyBox === box) return;
      if (this._buyBoxObserver) this._buyBoxObserver.disconnect();
      this._buyBox = box;
      /* two thresholds (in at 0%, out past the bar's own height) so a buy box sitting on the edge never makes the bar
         flicker. P17-26 (eye pass R2 P3-13): the margin was 15% of the viewport (140 px at 430), so the bar sat over a buy
         row already on the first screen; the bar's own height yields to it */
      this._buyBoxObserver = new IntersectionObserver(
        function (entries) {
          var inView = entries.some(function (entry) { return entry.isIntersecting; });
          if (inView && self.openPick) self.closePick(true);
          self.classList.toggle('is-buybox-visible', inView);
        },
        { rootMargin: '0px 0px -' + Math.max(56, Math.round(self.offsetHeight || 64)) + 'px 0px', threshold: [0, 0.01] }
      );
      this._buyBoxObserver.observe(box);
    }

    /* --- THE MENU ------------------------------------------------------------- */

    wirePick(pick, kind) {
      if (!pick) return;
      var self = this;
      var trigger = pick.querySelector('[data-svnty-pick-trigger]');
      var menu = pick.querySelector('[data-svnty-pick-menu]');
      if (!trigger || !menu) return;
      pick.dataset.kind = kind;

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (self.openPick === pick) self.closePick();
        else self.openPickMenu(pick);
      }, { signal: this._abort.signal });

      menu.addEventListener('keydown', function (event) {
        var options = Array.prototype.slice.call(menu.querySelectorAll('[role="option"]:not([aria-disabled="true"])'));
        if (!options.length) return;
        var i = options.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          options[(i + 1) % options.length].focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          options[(i - 1 + options.length) % options.length].focus();
        } else if (event.key === 'Home') {
          event.preventDefault();
          options[0].focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          options[options.length - 1].focus();
        }
      }, { signal: this._abort.signal });
    }

    openPickMenu(pick) {
      if (this.openPick && this.openPick !== pick) this.closePick(true);
      var menu = pick.querySelector('[data-svnty-pick-menu]');
      var trigger = pick.querySelector('[data-svnty-pick-trigger]');
      if (!menu || !trigger) return;
      if (pick.dataset.kind === 'row') this.renderRowOptions();
      if (pick.dataset.kind === 'colour') this.renderColourOptions();
      menu.hidden = false;
      menu.classList.remove('is-closing');
      void menu.offsetWidth;
      menu.classList.add('is-open');
      pick.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.openPick = pick;
      var selected =
        menu.querySelector('[role="option"][aria-selected="true"]') ||
        menu.querySelector('[role="option"]:not([aria-disabled="true"])');
      if (selected) {
        try {
          selected.focus({ preventScroll: true });
        } catch (e) {
          selected.focus();
        }
    }
    }

    closePick(instant) {
      var pick = this.openPick;
      if (!pick) return;
      var menu = pick.querySelector('[data-svnty-pick-menu]');
      var trigger = pick.querySelector('[data-svnty-pick-trigger]');
      this.openPick = null;
      pick.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (!menu) return;
      if (instant || reduceMotion) {
        menu.classList.remove('is-open');
        menu.hidden = true;
        return;
    }
      menu.classList.add('is-closing');
      window.setTimeout(function () {
        menu.classList.remove('is-open');
        menu.classList.remove('is-closing');
        menu.hidden = true;
      }, MENU_CLOSE_MS);
    }

    makeOption(label, selected, disabled, extra) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'svnty-pdpbar__option';
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (disabled) btn.setAttribute('aria-disabled', 'true');
      btn.tabIndex = -1;
      if (extra) btn.appendChild(extra);
      var text = document.createElement('span');
      text.className = 'svnty-pdpbar__option-label';
      text.textContent = label;
      btn.appendChild(text);
      var note = document.createElement('span');
      note.className = 'svnty-pdpbar__option-note';
      note.textContent = disabled ? this.soldLabel : STR.in_stock || '';
      btn.appendChild(note);
      return btn;
    }

    renderRowOptions() {
      var self = this;
      var host = this.rowPick && this.rowPick.querySelector('[data-svnty-pick-options]');
      if (!host) return;
      while (host.firstChild) host.removeChild(host.firstChild);
      this.rows.forEach(function (row) {
        var available = self.rowAvailable(row.name);
        var btn = self.makeOption(row.name, row.name === self.selectedRow, !available, null);
        btn.addEventListener('click', function () {
          if (!available) return;
          self.chooseRow(row);
        });
        host.appendChild(btn);
      });
    }

    bubbleFor(info) {
      var dot = document.createElement('span');
      dot.className = 'svnty-pdpbar__bubble';
      dot.setAttribute('aria-hidden', 'true');
      if (info && info.hex) dot.style.backgroundColor = info.hex;
      else if (info && info.swatch) dot.style.backgroundImage = 'url(' + info.swatch + ')';
      return dot;
    }

    renderColourOptions() {
      var self = this;
      var host = this.colourPick && this.colourPick.querySelector('[data-svnty-pick-options]');
      if (!host) return;
      while (host.firstChild) host.removeChild(host.firstChild);
      this.colours.forEach(function (colour) {
        var available = self.colourAvailable(colour.name);
        /* THE STRIPE NAME (D8-8, R239): the printed name ("Blue stripe", the snippet's label); the value stays colour.name */
        var btn = self.makeOption(colour.label || colour.name, colour.name === self.selectedColour, !available, self.bubbleFor(colour));
        btn.addEventListener('click', function () {
          self.chooseColour(colour);
        });
        host.appendChild(btn);
      });
    }

    chooseRow(row) {
      this.selectedRow = row.name;
      this.closePick();
      this.clearStatus();
      this.renderPicks();
      this.updateAddButton();
      clickPageOption(this.rowOption, row.name);
    }

    chooseColour(colour) {
      this.selectedColour = colour.name;
      if (this.selectedRow && !this.rowAvailable(this.selectedRow)) this.selectedRow = '';
      this.closePick();
      this.clearStatus();
      this.renderPicks();
      this.renderImage();
      this.updateAddButton();
      /* the one setter: the page (and everything that follows the bus) changes with the bar */
      var bus = window.SVNTY && window.SVNTY.colour;
      if (!(bus && bus.set(colour.name, 'sticky'))) clickPageOption(this.colourOption, colour.name);
    }

    renderPicks() {
      if (this.rowPick) {
        var label = this.rowPick.querySelector('[data-svnty-pick-label]');
        if (label) label.textContent = this.selectedRow || this.rowLabel;
        this.rowPick.classList.toggle('is-chosen', !!this.selectedRow);
    }
      if (this.colourPick) {
        var info = this.colourInfo(this.selectedColour);
        var bubble = this.colourPick.querySelector('[data-svnty-colour-bubble]');
        if (bubble) {
          bubble.style.backgroundColor = info && info.hex ? info.hex : '';
          bubble.style.backgroundImage = info && !info.hex && info.swatch ? 'url(' + info.swatch + ')' : '';
        }
        var srLabel = this.colourPick.querySelector('[data-svnty-pick-label]');
        if (srLabel) srLabel.textContent = this.colourLabel + ': ' + ((info && info.label) || this.selectedColour || '');
    }
    }

    renderImage() {
      if (!this.img) return;
      var info = this.hasColour ? this.colourInfo(this.selectedColour) : null;
      var url = info && info.image ? info.image : '';
      /* no variant picture for this colour: the colour's first gallery picture (THE TRUE SWATCH, read from the slide's
         data-svnty-colour since D5-1 A-07: the page's alts carry no dash to parse) */
      if (!url && this.hasColour && this.selectedColour) {
        var section = productSection();
        var tags = section ? section.querySelectorAll('.product__media-item [data-svnty-colour]') : [];
        for (var i = 0; i < tags.length; i += 1) {
          if (tags[i].getAttribute('data-svnty-colour') !== this.selectedColour) continue;
          var im = tags[i].querySelector('img');
          if (im) { url = im.currentSrc || im.getAttribute('src') || ''; break; }
        }
      }
      if (!url) return;
      var next = withWidth(url, 160);
      this.img.setAttribute('data-svnty-colour', this.selectedColour || '');
      if (this.img.getAttribute('src') === next) return;
      this.img.removeAttribute('srcset');
      this.img.setAttribute('src', next);
    }

    /* --- the counter ---------------------------------------------------------- */

    stepQty(delta) {
      var next = this.quantity + delta;
      if (!isFinite(next)) next = 1;
      this.quantity = Math.max(1, Math.min(99, Math.floor(next)));
      this.renderQty();
      this.clearStatus();
    }

    renderQty() {
      if (this.qtyVal) this.qtyVal.textContent = String(this.quantity);
      if (this.qtyDec) this.qtyDec.disabled = this.quantity <= 1;
    }

    /* --- ADD TO BAG ----------------------------------------------------------- */

    setAddState(state) {
      if (!this.addBtn) return;
      var soldOut = state === 'soldout';
      this.addBtn.classList.toggle('is-soldout', soldOut);
      this.addBtn.disabled = soldOut;
      if (soldOut) {
        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.textContent = this.soldLabel;
      } else {
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.textContent = state === 'select' ? this.selectLabel : this.addLabel;
    }
    }

    updateAddButton() {
      if (this.busy || !this.addBtn) return;
      var anyAvailable = this.variants.some(function (x) { return x.available; });
      if (!anyAvailable) {
        this.setAddState('soldout');
        return;
    }
      if (this.single) {
        var first = this.variants.find(function (x) { return x.available; }) || this.variants[0];
        this.setAddState(first && first.available ? 'add' : 'soldout');
        return;
    }
      if (this.hasColour && !this.colourAvailable(this.selectedColour)) {
        this.setAddState('soldout');
        return;
    }
      if (this.rows.length && !this.selectedRow) {
        /* a tap prompts the size — the button names the next action, never a dead CTA */
        this.setAddState('select');
        return;
    }
      var variant = this.resolveVariant();
      this.setAddState(variant && variant.available ? 'add' : 'soldout');
    }

    clearStatus() {
      if (this.status) this.status.textContent = '';
      if (this.rowPick) this.rowPick.classList.remove('is-invalid');
    }

    /* R3-5 (round 3). Every refusal this bar can hit — pick-a-size, sold out, a Shopify `status`
       body, and the `.catch` where a throttled 429 lands (Shopify answers a throttled add with an
       HTML body, so `response.json()` throws) — used to be written ONLY into
       `.svnty-pdpbar__status`, which is `visually-hidden`: a 1x1 node with `clip: rect(0,0,0,0)`.
       Measured: the spinner stopped and NOTHING on screen changed. The visible feedback layer on
       this store is the cart toast (`role="status"`, `aria-live="polite"`) — the same layer the
       bar's SUCCESS path already speaks through. Errors go there. The hidden node stays as the
       fallback for a page without the toast asset, and is un-hidden in that case so the words are
       at least on screen; when the toast speaks, the node is cleared so AT hears one message, not
       two. */
    say(message) {
      if (!message) return;
      var toast = window.SVNTYToast;
      if (toast && typeof toast.show === 'function') {
        if (this.status) this.status.textContent = '';
        toast.show(message);
        return;
      }
      if (this.status) {
        this.status.classList.remove('visually-hidden');
        this.status.textContent = message;
      }
    }

    onAdd() {
      if (this.busy) return;
      var variant = this.resolveVariant();
      if (!variant) {
        if (this.rowPick) {
          this.rowPick.classList.add('is-invalid');
          this.openPickMenu(this.rowPick);
        }
        this.say(this.selectLabel);
        return;
    }
      if (!variant.available) {
        this.say(this.soldLabel);
        return;
    }
      this.add(String(variant.id));
    }

    add(variantId) {
      var self = this;
      this.busy = true;
      if (this.addBtn) {
        this.addBtn.classList.add('is-loading');
        this.addBtn.disabled = true;
    }

      var cart = cartElement();
      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', String(this.quantity || 1));
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append(
          'sections',
          cart.getSectionsToRender().map(function (section) { return section.id; })
        );
        body.append('sections_url', window.location.pathname);
      } else {
        /* D-2: `cart_type` is `page`, so cartElement() is null and this road used to end in
           `window.location = /cart` - which also made the publish below dead code. The bar now
           adds IN PLACE: ask for the one section that carries the header's bag mark, and let
           assets/svnty-cart-toast.js paint it and raise the toast off the publish. */
        body.append('sections', 'cart-icon-bubble');
        body.append('sections_url', window.location.pathname);
    }

      /* P28 lane E (cart P2-4): a 429 / 503 add is asked again once (svntyAddFetch, global.js); a refused answer is an error, not JSON */
      var stickyAdd = { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: body };
      (typeof svntyAddFetch === 'function' ? svntyAddFetch(cartAddUrl(), stickyAdd) : fetch(cartAddUrl(), stickyAdd).then(function (response) { return response.json(); }))
        .then(function (payload) {
          if (payload && payload.status) {
            /* R3-24's shape: `description` can be absent while `message` carries the words, and a
               default parameter would swallow `undefined` — so read both, then the house string. */
            self.say(payload.description || payload.message || STR.add_error || '');
            /* P28 lane E (launch audit cart P2-7): a partial add (two asked, one left: the store adds one and answers 422) changed the
               bag; the refusal is published as the form's is, so the toast's askBag repaints the header's count from the store */
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartError) {
                publish(PUB_SUB_EVENTS.cartError, { source: 'svnty-pdp-sticky', productVariantId: variantId, errors: payload.errors || payload.description, message: payload.message });
              }
            } catch (e) {}
            return;
          }
          if (!cart && !window.SVNTYToast) {
            /* the degradation, not the road: only when the feedback layer is missing */
            window.location = cartUrl();
            return;
          }
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'svnty-pdp-sticky',
                productVariantId: variantId,
                cartData: payload
              });
            }
          } catch (e) {}
          if (cart && typeof cart.renderContents === 'function') cart.renderContents(payload);
          /* R3-20 (round 3): the ADDED label was set here and then overwritten in the NEXT
             microtask by the finalizer's `updateAddButton()`, so the confirmation was on screen
             for ~0 ms and the 1300 ms timer below was dead code. A flag the finalizer respects
             makes the flash real; the toast raised off the publish above is the announcement, so
             the hidden status node is left clear (one message, not two). */
          self.flashing = true;
          if (self.addBtn) self.addBtn.textContent = self.addedLabel;
          if (self.status) self.status.textContent = '';
          self.quantity = 1;
          self.renderQty();
          window.setTimeout(function () {
            self.flashing = false;
            self.updateAddButton();
          }, FLASH_MS);
        })
        .catch(function () {
          self.say(STR.add_error || '');
        })
        .then(function () {
          if (self.addBtn) self.addBtn.classList.remove('is-loading');
          self.busy = false;
          if (self.flashing) return;   /* R3-20: never overwrite the ADDED flash */
          self.updateAddButton();
        });
    }

  }

  if ('customElements' in window && !customElements.get('svnty-pdp-sticky')) {
    customElements.define('svnty-pdp-sticky', SvntyPdpSticky);
  }
})();
